import type { MeshTransportBus } from '../types/transport.js';
import type { CollabEnvelope } from '../types/envelope.js';
import { parseHtsUri, parseHnsUri } from '../validation/uri-validation.js';
import { validateEnvelope, parsePluginNamespace } from '../validation/envelope-validation.js';
import { parseEventId } from '../validation/uuid-validation.js';
import { SecurityTier } from '../types/security.js';
import type { HostBroadcastService, Result } from './types.js';

function generateUuidV7(): string {
  const now = Date.now();
  const timeHex = now.toString(16).padStart(12, '0');
  const randA = Math.floor(Math.random() * 0x0fff).toString(16).padStart(3, '0');
  const randB = (Math.floor(Math.random() * 0x3fff) | 0x8000).toString(16).padStart(4, '0');
  const randC = Math.floor(Math.random() * 0xffffffffffff).toString(16).padStart(12, '0');
  return `${timeHex.slice(0, 8)}-${timeHex.slice(8, 12)}-7${randA}-${randB}-${randC}`;
}

export class BroadcastService implements HostBroadcastService {
  constructor(
    private readonly nodeId: string,
    private readonly bus: MeshTransportBus,
    private readonly idGenerator: () => string = generateUuidV7
  ) {}

  async publish(destinationUri: string, payload: unknown): Promise<Result<void>> {
    try {
      const parsedDest = parseHtsUri(destinationUri);
      const sourceHns = parseHnsUri(`hns://${this.nodeId}.mesh`);
      const ns = parsePluginNamespace(parsedDest.plugin);

      const rawEnv: Record<string, unknown> = {
        protocolVersion: 1,
        eventId: parseEventId(this.idGenerator()),
        sourceUri: sourceHns,
        destinationUri: parsedDest.canonical,
        pluginId: ns,
        senderId: this.nodeId,
        createdAt: Date.now(),
        securityTier: SecurityTier.CLASSICAL,
        payload,
      };

      const envelope = validateEnvelope(rawEnv);
      await this.bus.receive(envelope);
      await this.bus.dispatch(parsedDest.canonical, envelope);
      return { ok: true, value: undefined };
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      return {
        ok: false,
        error: {
          type: 'INVALID_URI',
          code: 'ERR_PUBLISH_FAILED',
          message,
        },
      };
    }
  }

  subscribe(destinationUri: string, handler: (envelope: CollabEnvelope<unknown>) => void): () => void {
    try {
      const parsedDest = parseHtsUri(destinationUri);
      return this.bus.subscribe(parsedDest.canonical, async (env) => {
        handler(env);
      });
    } catch {
      return () => undefined;
    }
  }
}
